import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Drawer } from 'antd';
import SubscriptionStep, { Mode } from './SubscriptionStep';
import SubscriptionWithTermStep from './SubscriptionWithTermStep';
import useScreenType from '../../hooks/context/useScreenType';
import useApiClient from '../../hooks/api/useApiClient';
import { useGetServiceContractQuery } from '../../store/api-slices';

/**
 * The service-contract commitment (term + annual discount) is a master-only
 * feature. Master service providers get SubscriptionWithTermStep; everyone
 * else keeps the legacy SubscriptionStep flow.
 *
 * When rendered for an existing subscription (`data` set) this also mirrors the
 * billing view's edit flow: the step shows in preview with an edit action that
 * opens a drawer rendering the same step in edit mode. Once the service
 * contract is signed the subscription is locked, so the edit action is hidden.
 */
export default function SubscriptionStepStrategy(props) {
  const isMaster = useSelector((state) => state.currentServiceProvider.serviceProvider?.is_master);
  const { isDesktop } = useScreenType();

  const { data, onEdit, onSave } = props;

  const { ServiceContractsManager } = useApiClient();
  // The step components fetch the same contract; this shares the RTK Query cache.
  const { data: serviceContract } = useGetServiceContractQuery(
    { apiManager: ServiceContractsManager },
    { refetchOnMountOrArgChange: true },
  );
  const isContractSigned = !!serviceContract?.signed_at;

  const StepComponent = isMaster ? SubscriptionWithTermStep : SubscriptionStep;

  const [showEditDrawer, setShowEditDrawer] = useState(false);

  const toggleEditDrawer = (open) => setShowEditDrawer(open);

  const resolvedOnEdit = isContractSigned ? undefined : data ? () => toggleEditDrawer(true) : onEdit;

  return (
    <>
      <StepComponent {...props} onEdit={resolvedOnEdit} />
      {showEditDrawer && (
        <Drawer open size={window.innerWidth * (isDesktop ? 0.5 : 1)} onClose={() => toggleEditDrawer(false)}>
          <StepComponent
            {...props}
            mode={Mode.edit}
            moveForward={undefined}
            onSave={() => {
              toggleEditDrawer(false);
              onSave && onSave();
            }}
          />
        </Drawer>
      )}
    </>
  );
}
